import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, ShieldAlert, Plus, Trash2, Pencil, Crown, X, HardHat, Check, Ban } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", excerpt: "", body: "", cover_url: "", tags: "", vip_only: false, published: true };

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [editing, setEditing] = useState(null); // post_id or "new" or null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const loadBlog = () => api.get("/admin/blog").then(({ data }) => setPosts(data)).catch(() => {});
  const loadContractors = () => api.get("/admin/contractors").then(({ data }) => setContractors(data)).catch(() => {});

  useEffect(() => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/leads"), api.get("/admin/contacts"), api.get("/admin/blog"), api.get("/admin/contractors")])
      .then(([s, l, c, b, k]) => { setStats(s.data); setLeads(l.data); setContacts(c.data); setPosts(b.data); setContractors(k.data); })
      .catch((e) => { if (e.response?.status === 403) setDenied(true); })
      .finally(() => setLoading(false));
  }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => {
    setForm({ title: p.title, excerpt: p.excerpt || "", body: p.body || "", cover_url: p.cover_url || "", tags: (p.tags || []).join(", "), vip_only: !!p.vip_only, published: !!p.published, slug: p.slug });
    setEditing(p.post_id);
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and body are required.");
    setSaving(true);
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing === "new") await api.post("/admin/blog", payload);
      else await api.put(`/admin/blog/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      loadBlog();
    } catch (e) {
      toast.error("Could not save post");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/blog/${id}`); loadBlog(); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const setContractorStatus = async (id, status) => {
    try {
      await api.post(`/admin/contractors/${id}/status`, { status });
      loadContractors();
      toast.success(status === "approved" ? "Contractor approved & vetted" : "Contractor rejected");
    } catch { toast.error("Could not update contractor"); }
  };

  if (loading)
    return <div className="min-h-screen bp-grid"><Navbar /><div className="flex justify-center py-32"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div></div>;

  if (denied || user?.role !== "admin")
    return (
      <div className="min-h-screen bp-grid"><Navbar />
        <div className="mx-auto flex max-w-md flex-col items-center px-4 py-28 text-center">
          <ShieldAlert className="h-12 w-12 text-destructive" />
          <h1 className="mt-4 font-heading text-2xl font-bold">Admins only</h1>
          <p className="mt-2 text-muted-foreground">You don't have access to this page.</p>
        </div>
      </div>
    );

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");
  const fieldCls = "mt-1.5 rounded-sm border-border bg-transparent";
  const lbl = "font-mono2 text-xs uppercase tracking-widest text-muted-foreground";

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
        <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">Admin</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tighter sm:text-4xl">Command center</h1>

        <div className="mt-6 grid grid-cols-2 border border-border sm:grid-cols-5">
          {stats && [
            ["Users", stats.users], ["Projects", stats.projects], ["Paid", stats.paid_customers],
            ["Leads", stats.leads], ["Messages", stats.contacts],
          ].map(([l, v], i) => (
            <div key={i} className="border-r border-border p-5 last:border-r-0">
              <div className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
              <div className="mt-1 font-mono2 text-2xl font-bold text-primary">{v}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="blog" className="mt-10">
          <TabsList className="rounded-sm bg-secondary/40">
            <TabsTrigger value="blog" data-testid="admin-tab-blog" className="font-mono2 text-xs uppercase tracking-widest">Blog ({posts.length})</TabsTrigger>
            <TabsTrigger value="contractors" data-testid="admin-tab-contractors" className="font-mono2 text-xs uppercase tracking-widest">Contractors ({contractors.length})</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads" className="font-mono2 text-xs uppercase tracking-widest">Waitlist ({leads.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="font-mono2 text-xs uppercase tracking-widest">Messages ({contacts.length})</TabsTrigger>
          </TabsList>

          {/* BLOG */}
          <TabsContent value="blog" className="mt-4">
            {editing ? (
              <div className="border border-border p-6" data-testid="blog-editor">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold">{editing === "new" ? "New post" : "Edit post"}</h3>
                  <Button variant="ghost" size="icon" onClick={() => setEditing(null)} data-testid="blog-close"><X className="h-4 w-4" /></Button>
                </div>
                <div className="space-y-4">
                  <div><Label className={lbl}>Title</Label><Input data-testid="blog-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={fieldCls} /></div>
                  <div><Label className={lbl}>Excerpt</Label><Input data-testid="blog-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={fieldCls} placeholder="Optional short summary" /></div>
                  <div><Label className={lbl}>Body</Label><Textarea data-testid="blog-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={8} className={fieldCls} placeholder="Write your post. Separate paragraphs with a blank line." /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className={lbl}>Cover image URL</Label><Input data-testid="blog-cover" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} className={fieldCls} placeholder="/napkin-to-home.jpg" /></div>
                    <div><Label className={lbl}>Tags (comma separated)</Label><Input data-testid="blog-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={fieldCls} placeholder="pricing, worldwide" /></div>
                  </div>
                  <div className="flex flex-wrap gap-8">
                    <label className="flex items-center gap-2"><Switch checked={form.vip_only} onCheckedChange={(v) => setForm({ ...form, vip_only: v })} data-testid="blog-vip" /><span className={lbl}>VIP only</span></label>
                    <label className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="blog-published" /><span className={lbl}>Published</span></label>
                  </div>
                  <Button onClick={save} disabled={saving} data-testid="blog-save" className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save Post
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Button onClick={openNew} data-testid="blog-new-btn" className="mb-4 rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                  <Plus className="mr-2 h-4 w-4" /> New Post
                </Button>
                <div className="border border-border">
                  {posts.length === 0 ? (
                    <p className="p-6 text-center font-mono2 text-xs uppercase tracking-widest text-muted-foreground">No posts yet</p>
                  ) : posts.map((p) => (
                    <div key={p.post_id} className="flex items-center justify-between gap-3 border-b border-border px-5 py-3 last:border-b-0" data-testid={`blog-row-${p.slug}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {p.vip_only && <Crown className="h-3.5 w-3.5 text-primary" />}
                          <span className="truncate font-heading font-semibold">{p.title}</span>
                          {!p.published && <span className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">(draft)</span>}
                        </div>
                        <span className="font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">/{p.slug} · {fmt(p.created_at)}</span>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button variant="outline" size="icon" onClick={() => openEdit(p)} data-testid={`blog-edit-${p.slug}`} className="rounded-sm border-border"><Pencil className="h-4 w-4" /></Button>
                        <Button variant="outline" size="icon" onClick={() => del(p.post_id)} data-testid={`blog-delete-${p.slug}`} className="rounded-sm border-border text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="contractors" className="mt-4">
            <div className="border border-border">
              {contractors.length === 0 ? (
                <p className="p-6 text-center font-mono2 text-xs uppercase tracking-widest text-muted-foreground">No applications yet</p>
              ) : contractors.map((c) => (
                <div key={c.contractor_id} className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3 last:border-b-0" data-testid={`contractor-admin-row-${c.contractor_id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <HardHat className="h-3.5 w-3.5 text-primary" />
                      <span className="truncate font-heading font-semibold">{c.company}</span>
                      <span className={`border px-1.5 py-0.5 font-mono2 text-[9px] uppercase tracking-widest ${
                        c.status === "approved" ? "border-green-500/50 text-green-400" : c.status === "rejected" ? "border-red-500/50 text-red-400" : "border-amber-500/50 text-amber-400"
                      }`}>{c.status}</span>
                    </div>
                    <span className="font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">
                      {c.email} · {[c.city, c.country].filter(Boolean).join(", ") || "no location"} · {(c.trades || []).join(", ") || "no trades"}
                    </span>
                    {c.bio && <p className="mt-1 max-w-xl truncate text-xs text-muted-foreground">{c.bio}</p>}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {c.status !== "approved" && (
                      <Button size="sm" onClick={() => setContractorStatus(c.contractor_id, "approved")} data-testid={`approve-contractor-${c.contractor_id}`}
                        className="rounded-sm bg-primary font-mono2 text-[10px] uppercase tracking-widest text-black hover:bg-primary/90">
                        <Check className="mr-1 h-3.5 w-3.5" /> Approve
                      </Button>
                    )}
                    {c.status !== "rejected" && (
                      <Button size="sm" variant="outline" onClick={() => setContractorStatus(c.contractor_id, "rejected")} data-testid={`reject-contractor-${c.contractor_id}`}
                        className="rounded-sm border-border font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-destructive">
                        <Ban className="mr-1 h-3.5 w-3.5" /> Reject
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="leads" className="mt-4">
            <div className="border border-border">
              <div className="grid grid-cols-3 gap-3 border-b border-border bg-secondary/30 px-5 py-3 font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">
                <span>Email</span><span>Source</span><span>Date</span>
              </div>
              {leads.length === 0 ? (
                <p className="p-6 text-center font-mono2 text-xs uppercase tracking-widest text-muted-foreground">No leads yet</p>
              ) : leads.map((l) => (
                <div key={l.lead_id} className="grid grid-cols-3 gap-3 border-b border-border px-5 py-3 font-mono2 text-sm last:border-b-0">
                  <span className="truncate text-foreground">{l.email}</span>
                  <span className="text-muted-foreground">{l.source}</span>
                  <span className="text-muted-foreground">{fmt(l.created_at)}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-4">
            <div className="space-y-px">
              {contacts.length === 0 ? (
                <p className="border border-border p-6 text-center font-mono2 text-xs uppercase tracking-widest text-muted-foreground">No messages yet</p>
              ) : contacts.map((c) => (
                <div key={c.contact_id} className="border border-border p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-heading font-semibold">{c.name} <span className="font-mono2 text-xs font-normal text-muted-foreground">· {c.email}</span></span>
                    <span className="font-mono2 text-xs text-muted-foreground">{fmt(c.created_at)}</span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{c.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
